import {
  assembleOfferTemplate,
  buildDefaultShell,
  ensureOfferLetterChrome,
  applyOfferLayoutToHtml,
  buildPreviewHtml,
  isSectionVisible,
  fillStaleSectionDrafts,
  relocateMarkedSectionsIntoContent,
  repairOfferBodyAfterChrome,
  repairOfferSalaryForPdfFromDraft,
  ensureOfferContentWrapper,
} from '@/utils/offer-template-editor'

export function parseJson(value, fallback = {}) {
  if (!value) return fallback
  if (typeof value === 'object') return value
  try {
    return JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

export function estimateJsonPayloadBytes(payload) {
  try {
    return new TextEncoder().encode(JSON.stringify(payload)).length
  } catch (e) {
    return 0
  }
}

/** Plain text snippet for verifying letter fields were written into saved HTML. */
export function plainTextSnippet(htmlOrText, maxLen = 48) {
  if (!htmlOrText) return ''
  const withoutBreakUi = String(htmlOrText)
    .replace(
      /(?:<!--\s*OFFER_MANUAL_PAGE_BREAK\s*-->\s*)?<div[^>]*class="[^"]*\boffer-manual-page-break\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
      ' ',
    )
    .replace(
      /(?:<!--\s*OFFER_(?:[A-Z_]*PAGE_BREAK)\s*-->\s*)?<div[^>]*class="[^"]*\boffer-page-break\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi,
      ' ',
    )
  const plain = withoutBreakUi
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&apos;|&#39;/gi, "'")
    .replace(/&quot;|&#34;/gi, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/\s*&\s*/g, ' and ')
    .replace(/\s+/g, ' ')
    .trim()
  return plain.slice(0, maxLen)
}

export function htmlContainsPlainSnippet(html, snippet) {
  if (!snippet || snippet.length < 12) return true
  const haystack = plainTextSnippet(html, 500000).toLowerCase()
  const needle = String(snippet).toLowerCase()
  if (haystack.includes(needle)) return true
  // Layout may rewrite the section title; match the distinctive remainder.
  const withoutTitle = needle
    .replace(/^employment policies and provisions\s*/i, '')
    .replace(/^job responsibilities\s*/i, '')
    .replace(/^terms and conditions(?: of employment)?\s*/i, '')
    .trim()
  return withoutTitle.length >= 12 && haystack.includes(withoutTitle)
}

/**
 * Canonical offer HTML for save / PDF / live preview:
 * assemble → chrome → layout (with structured-mode content fallback).
 * Pure — does not mutate the form.
 */
export function buildOfferGenerationHtml({
  htmlTemplate = '',
  templateEditor = {},
  chromeOptions = {},
  offerSectionDrafts = null,
} = {}) {
  const mode = templateEditor.mode
  const chromeOpts = chromeOptions || {}
  const drafts = offerSectionDrafts
    || templateEditor.offerSectionDrafts
    || null

  if (mode === 'raw') {
    return ensureOfferLetterChrome(htmlTemplate || '', chromeOpts)
  }

  const baseHtml = htmlTemplate || buildDefaultShell(templateEditor.fields || {})
  // Policies ON + empty/broken parsed body but draft has content (replicate hydrate).
  let fields = fillStaleSectionDrafts(
    templateEditor.fields || {},
    drafts,
    baseHtml,
  )
  // Preview/generate chrome may pass signatureSrc without embedding it on save.
  // Merge into fields so Closing-OFF stamp reapply keeps a real img src.
  const previewStampRaw = chromeOpts.signatureSrc
    || chromeOpts.signature
    || (chromeOpts.images && chromeOpts.images.signature)
    || ''
  const previewStamp = typeof previewStampRaw === 'string'
    ? previewStampRaw.trim()
    : (previewStampRaw && typeof previewStampRaw === 'object'
      ? String(previewStampRaw.url || previewStampRaw.src || previewStampRaw.base64 || '').trim()
      : '')
  if (previewStamp && !/\{\{\s*METADATA\.IMAGES\.SIGNATURE\s*\}\}/i.test(previewStamp)) {
    fields = {
      ...fields,
      signatureSrc: previewStamp,
      signature: previewStamp,
      images: { ...(fields.images || {}), signature: previewStamp },
    }
  }
  let html = assembleOfferTemplate(baseHtml, fields, {
    ...templateEditor,
    fields,
  })

  // If assemble dropped Quill edits, insert into the existing document first
  // (keeps two salary tables / branding). Rebuild from a marked shell only
  // when the stored HTML has no Policies slot at all.
  const introSnippet = plainTextSnippet(fields.intro)
  const bodySnippet = plainTextSnippet(fields.body)
  const introMissing = () => isSectionVisible(fields, 'intro')
    && introSnippet.length >= 12
    && !htmlContainsPlainSnippet(html, introSnippet)
  const bodyMissing = () => isSectionVisible(fields, 'body')
    && bodySnippet.length >= 12
    && !htmlContainsPlainSnippet(html, bodySnippet)
  if (bodyMissing()) {
    html = repairOfferBodyAfterChrome(html, fields)
  }
  if (introMissing() || bodyMissing()) {
    const preservedSalary = fields.salaryStructure || ''
    html = assembleOfferTemplate(
      buildDefaultShell({ ...fields, salaryStructure: preservedSalary }),
      { ...fields, salaryStructure: preservedSalary },
      { mode: 'structured', fields, regions: null },
    )
  }

  // Stamp / placement chrome (assemble already chromed once; this applies latest form opts).
  html = ensureOfferLetterChrome(html || '', chromeOpts)
  html = applyOfferLayoutToHtml(html, fields.offerLayout)
  // Second chrome can drop OFFER_BODY / `.content` — restore Policies for live preview.
  html = repairOfferBodyAfterChrome(html, fields)
  html = repairOfferSalaryForPdfFromDraft(html, fields, { offerSectionDrafts: drafts })
  return relocateMarkedSectionsIntoContent(ensureOfferContentWrapper(html))
}

/**
 * Sample-filled document HTML for iframe srcdoc and Preview PDF (WYSIWYG).
 * @param {string} generationHtml
 * @param {{ jobTitle?: string, location?: string, images?: object, mode?: 'screen'|'pdf' }} [options]
 *   mode 'screen' (default): discrete A4 page cards for live preview.
 *   mode 'pdf': keep print page-breaks + placement markers for PDF chrome.
 */
export function buildOfferPreviewDocumentHtml(generationHtml, {
  jobTitle,
  location,
  images,
  mode = 'screen',
} = {}) {
  if (!generationHtml) return ''
  return buildPreviewHtml(generationHtml, { jobTitle, location, images, mode })
}
